import { useCallback, useEffect, useRef, useState } from "react";
import styles from "../../styles/components/PersonImportModal.module.css";
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";
import { contactsService } from "../../services/contacts.service";
import { useModalFocusTrap } from "../mailContent/useModalFocusTrap";
import { useOverlayClose } from "../../hooks/useOverlayClose";

const IN_PROGRESS_STATUSES = ["queued", "validating", "processing"];

const PROBLEM_KIND_LABELS = {
  file_error: "Error en el archivo",
  not_validated: "No validado",
  not_saved: "No se pudo guardar",
};

function joinParts(parts) {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} y ${parts[parts.length - 1]}`;
}

function describeImportedContacts(result) {
  const parts = [];
  if (result.created) {
    parts.push(
      result.created === 1
        ? "se creó 1 contacto nuevo"
        : `se crearon ${result.created} contactos nuevos`,
    );
  }
  if (result.updated) {
    parts.push(result.updated === 1 ? "se completó 1" : `se completaron ${result.updated}`);
  }
  if (result.unchanged) {
    parts.push(
      result.unchanged === 1 ? "1 quedó sin cambios" : `${result.unchanged} quedaron sin cambios`,
    );
  }
  if (parts.length === 0) return "No se importó ningún contacto.";
  const sentence = joinParts(parts);
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

export default function PersonImportResultModal({ importId, onClose, onLoaded }) {
  const [personImport, setPersonImport] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const handleClose = useCallback(() => onCloseRef.current(), []);
  useModalFocusTrap(dialogRef, closeButtonRef, handleClose);

  useEffect(() => {
    let cancelled = false;
    setPersonImport(null);
    setLoadError(null);
    contactsService
      .getImport(importId)
      .then((importData) => {
        if (cancelled) return;
        setPersonImport(importData);
        onLoadedRef.current?.(importData);
      })
      .catch(() => {
        if (!cancelled) setLoadError("No se pudo cargar el resultado del import.");
      });
    return () => {
      cancelled = true;
    };
  }, [importId]);

  const overlayCloseProps = useOverlayClose(onClose);

  const result = personImport?.result;
  const problems = result?.problems ?? [];
  const isInProgress = IN_PROGRESS_STATUSES.includes(personImport?.status);

  return (
    <div className={styles.overlay} {...overlayCloseProps}>
      <div
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="person-import-result-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div>
            <div id="person-import-result-title" className={styles.title}>
              Resultado del import
            </div>
            <div className={styles.subtitle}>{personImport?.file_name ?? ""}</div>
          </div>
          <button
            ref={closeButtonRef}
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Cerrar"
            type="button"
          >
            <CrossIcon className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.body}>
          {!personImport && !loadError && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              Cargando el resultado…
            </div>
          )}

          {loadError && (
            <div className={styles.errorText} role="alert">
              {loadError}
            </div>
          )}

          {isInProgress && (
            <div className={styles.notice} role="status">
              {personImport.status === "validating"
                ? "BounceBan todavía está validando los emails. Te vamos a avisar con una notificación cuando termine."
                : "El import todavía se está procesando. Te vamos a avisar con una notificación cuando termine."}
            </div>
          )}

          {personImport?.status === "failed" && (
            <div className={`${styles.notice} ${styles.noticeError}`} role="alert">
              {personImport.failure_reason || "No se pudo completar el import."}
            </div>
          )}

          {personImport?.status === "finished" && result && (
            <>
              <div className={styles.resultTitle}>{describeImportedContacts(result)}</div>
              {problems.length === 0 ? (
                <div className={styles.notice} role="status">
                  Se importaron todas las filas del archivo.
                </div>
              ) : (
                <>
                  <div className={styles.summary}>
                    <span className={`${styles.summaryChip} ${styles.summaryChipError}`}>
                      <strong>{problems.length}</strong>{" "}
                      {problems.length === 1 ? "fila no se importó" : "filas no se importaron"}
                    </span>
                  </div>
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Fila</th>
                          <th>Contacto</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {problems.map((problem) => (
                          <tr key={problem.line} data-testid={`import-problem-${problem.line}`}>
                            <td className={styles.cellMuted}>{problem.line}</td>
                            <td>
                              <div>{problem.name || "-"}</div>
                              <div className={styles.cellMuted}>{problem.email || "-"}</div>
                            </td>
                            <td>
                              <span className={`${styles.actionBadge} ${styles.actionError}`}>
                                {PROBLEM_KIND_LABELS[problem.kind] ?? "No importado"}
                              </span>
                              <div className={styles.issueError}>{problem.reason}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.primaryBtn} onClick={onClose} type="button">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
